import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';

import { CommonModule } from '@angular/common';
import { ThemeService } from '../../services/theme.service';
import { FooterComponent } from '../footer/footer.component';
import { TranslationSyncService } from '../../services/translation-sync.service';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { take } from 'rxjs';
import { ContactFormStateService, ContactFormState } from '../../services/contact-form-state.service';
import { ContactService } from '../../services/contact.service';
import { LoggerService } from '../../services/logger.service';


@Component({
  selector: 'app-contact',
  imports: [CommonModule, FooterComponent, ReactiveFormsModule],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.css',
})
export class ContactComponent implements OnInit, AfterViewInit {

  contactForm!: FormGroup;
  @Input() activeFace: number = 0;
  isHidden = true;
  isDarkMode: boolean;
  @ViewChild('containerContact', { static: false }) containerRef!: ElementRef<HTMLElement>;
  @ViewChild('contactWrapper', { static: false }) contactWrapperRef!: ElementRef<HTMLElement>;

  @Output() toggleImprint = new EventEmitter<void>();
  @Output() togglePrivacy = new EventEmitter<void>();
  private darkContainer!: HTMLElement;
  showTitle = true;
  titleGone = false;


  // Zwei FormGroup-Instanzen
  contactFormLight!: FormGroup;
  contactFormDark!: FormGroup;
  submissionError: boolean;

  constructor(
    private fb: FormBuilder
    , private renderer: Renderer2
    , private themeService: ThemeService
    , private sync: TranslationSyncService
    , private formState: ContactFormStateService
    , private contactService: ContactService
    , private logger: LoggerService) { }

  ngOnInit(): void {

    // FormGroup bauen
    this.contactForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      subject: ['', Validators.required],
      message: ['', Validators.required],
    });

    // State aus Service in Form laden (erstes Mal)
    this.formState.values$.pipe(take(1)).subscribe(saved => {
      this.contactForm.patchValue(saved);
    });

    // Jede Form-Änderung zurück in den Service schreiben
    this.contactForm.valueChanges.subscribe((vals: ContactFormState) => {
      this.formState.update(vals);
    });

    this.themeService.isDarkMode$.subscribe(mode => {
      this.isDarkMode = mode;
      Promise.resolve().then(() => {
        const triggeredByToggle = this.themeService.wasThemeToggled();
        this.updateTheme(triggeredByToggle);
      });
    });
  }

  ngAfterViewInit(): void {
    const container = this.containerRef.nativeElement;
    const contactWrapper = this.containerRef.nativeElement;
    this.darkContainer = document.getElementById('dark-container-contact') as HTMLElement;
    const darkContactWrapper = this.darkContainer.querySelector<HTMLElement>('.contact-wrapper');


    if (!container || !this.darkContainer) return;

    // für data-translate bei spracheinstellung
    this.registerAll(this.containerRef.nativeElement);
    this.registerAll(this.darkContainer);

    // // 🔥 2️⃣ Dark-Container initial ausblenden
    this.renderer.removeClass(this.darkContainer, 'active');



    // 🔥 5️⃣ Theme sofort beim Laden aktualisieren
    Promise.resolve().then(() => {
      const triggeredByToggle = this.themeService.wasThemeToggled();
      this.updateTheme(triggeredByToggle);
    });

      setTimeout(() => {
      this.titleGone = true;


  }, 10000);

  }

  private updateTheme(triggeredByToggle: boolean): void {
    if (!this.darkContainer || !this.containerRef?.nativeElement) {
      return;
    }

    // ❶ Wwipe-Animation etc. unverändert
    this.themeService.toggleThemeMode(
      this.isDarkMode, this.renderer,
      this.containerRef.nativeElement,
      this.darkContainer
    );

    // ❷ Fülle die Dark-Inputs JEDES Mal mit den aktuellen Werten
    const darkForm = this.darkContainer.querySelector<HTMLFormElement>('.contact-form')!;

    this.ensureDarkErrorPlaceholders(darkForm);

    const values = this.contactForm.value;
    Object.entries(values).forEach(([k, v]) => {
      const inp = darkForm.querySelector<HTMLInputElement | HTMLTextAreaElement>(
        `[formcontrolname="${k}"], #${k}Field`
      );
      if (inp) inp.value = v as string;
    });

    this.syncTouchedStateAndErrors(darkForm);

    // ❸ Stelle sicher, dass die Listener nur einmal gesetzt werden
    if (!darkForm.hasAttribute('data-listeners-set')) {
      darkForm.setAttribute('data-listeners-set', 'true');

      // a) Live-Sync zurück in Angular
      darkForm.querySelectorAll('input,textarea').forEach(el => {
        el.addEventListener('input', () => {
          this.syncDarkToForm(darkForm);
        });
      });

      this.attachTouchedHandlers(darkForm);

      // b) Submit → Angular sendMessage()
      darkForm.addEventListener('submit', e => {
        e.preventDefault();
        this.syncDarkToForm(darkForm);
        this.updateDarkErrorMessages(darkForm);
        this.sendMessage();
      });
    }

    this.contactForm.statusChanges
      .pipe(take(1)) // einmal initial
      .subscribe(() => this.updateDarkErrorMessages(darkForm));
    this.contactForm.statusChanges
      .subscribe(() => this.updateDarkErrorMessages(darkForm));
  }

  private syncTouchedStateAndErrors(form: HTMLFormElement): void {
    Object.keys(this.contactForm.controls).forEach(key => {
      const ctrl = this.contactForm.get(key)!;

      if (ctrl.touched) {
        const input = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          `[formcontrolname="${key}"], #${key}Field`
        );
        if (input && !input.classList.contains('ng-touched')) {
          this.renderer.addClass(input, 'ng-touched');
        }
      }
    });

    this.updateDarkErrorMessages(form);
  }


  /** Liest alle Felder aus dem Dark-Form und patched contactForm accordingly */
  private syncDarkToForm(form: HTMLFormElement) {
    const patch: any = {};
    ['name', 'email', 'phone', 'subject', 'message'].forEach(field => {
      const el = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(
        `input[formcontrolname="${field}"],textarea[formcontrolname="${field}"],#${field}Field`
      );
      if (el) patch[field] = el.value;
    });
    this.contactForm.patchValue(patch, { emitEvent: false });
  }




  // falls Du später noch Props änderst:
  ngOnChanges(changes: SimpleChanges) {
    if (changes['someLangOrConfig']) {
      this.registerAll(this.containerRef.nativeElement);
      this.registerAll(this.darkContainer);
    }
  }

  get f() {
    return this.contactForm.controls;
  }

  ngAfterViewChecked() {
    this.registerAll(this.containerRef.nativeElement);
    this.registerAll(this.darkContainer);
  }



  sendMessage() {
    this.logger.debug('sendMessage!');
    const darkForm = this.darkContainer.querySelector<HTMLFormElement>('.contact-form');
    this.submissionError = false;
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const payload = this.contactForm.value;
    this.logger.debug('payload', payload);

    this.contactService.sendContact(payload).subscribe({
      next: _ => {
        this.logger.info('Mail verschickt!');
        this.contactForm.reset();
        this.formState.reset();

        if (darkForm) {
          darkForm.reset();
          this.toggleGlobalSubmissionError(darkForm, false);
        }
      },
      error: err => {
        this.logger.error('Fehler beim Mail-Versand', err);
        this.submissionError = true;
        this.updateDarkErrorMessages(this.darkContainer.querySelector('.contact-form')!);
        if (darkForm) {
          this.toggleGlobalSubmissionError(darkForm, true);
        }
        // TODO STATUS COde manuell setzen
        // this.contactForm.reset();
        // this.formState.reset();
        // const darkForm = this.darkContainer.querySelector<HTMLFormElement>('.contact-form');
        // if (darkForm) darkForm.reset();
      }
    });

  }

  private registerAll(root: HTMLElement) {

    const elems = Array.from(root.querySelectorAll<HTMLElement>('[data-translate]'));

    // Nur <input> mit placeholder-Key
    const elemsPlaceholderInput = Array.from(
      root.querySelectorAll<HTMLInputElement>(
        'input[data-translate-placeholder]'
      )
    );

    // Nur <textarea> mit placeholder-Key
    const elemsPlaceholderTextArea = Array.from(
      root.querySelectorAll<HTMLTextAreaElement>(
        'textarea[data-translate-placeholder]'
      )
    );

    elems.forEach(el => {
      const key = el.getAttribute('data-translate')!;
      this.sync.register(el, key);
    });

    // Dann separat registrieren
    elemsPlaceholderInput.forEach(el =>
      this.sync.registerPlaceholderInput(
        el,
        el.getAttribute('data-translate-placeholder')!
      )
    );
    elemsPlaceholderTextArea.forEach(el =>
      this.sync.registerPlaceholderTextArea(
        el,
        el.getAttribute('data-translate-placeholder')!
      )
    );
  }

  private toggleErrorDisplay(form: HTMLFormElement, selector: string, show: boolean) {
    const el = form.querySelector(selector) as HTMLElement;

    if (el) {
      this.renderer.setStyle(el, 'display', show ? 'block' : 'none');
    }
  }

  private updateDarkErrorMessages(form: HTMLFormElement) {
    const setErrorState = (field: string, selector: string, errorKey?: string) => {
      const hasErr = this.hasError(field, errorKey);
      this.toggleErrorDisplay(form, selector, hasErr);
      this.toggleInputClass(form, field, this.hasError(field));
    };

    setErrorState('name', '#nameError');
    setErrorState('email', '#emailRequiredError', 'required');
    setErrorState('email', '#emailInvalidError', 'email');
    setErrorState('subject', '#subjectError');
    setErrorState('message', '#messageError');


  }

  private hasError(field: string, error?: string): boolean {
    const ctrl = this.contactForm.get(field);
    if (!ctrl || !ctrl.touched) return false;
    return error ? ctrl.hasError(error) : ctrl.invalid;
  }

  private toggleInputClass(form: HTMLFormElement, field: string, invalid: boolean) {
    const inp = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      `[formcontrolname="${field}"], #${field}Field`
    );
    if (!inp) return;
    if (invalid) {
      this.renderer.addClass(inp, 'ng-invalid');
    } else {
      this.renderer.removeClass(inp, 'ng-invalid');
    }
  }

  private attachTouchedHandlers(form: HTMLFormElement) {
    form.querySelectorAll('input,textarea').forEach((el: any) => {
      el.addEventListener('blur', () => {
        const name = el.getAttribute('formcontrolname')!;
        // FormGroup-Flag
        this.contactForm.get(name)!.markAsTouched();
        // manuell die CSS-Klasse hinzufügen
        this.renderer.addClass(el, 'ng-touched');
        // und dann Fehlermeldungen & Styles updaten
        this.updateDarkErrorMessages(form);
      });
    });
  }

  /**
 * Legt für jedes Feld im Dark-Form einmalig die zugehörigen Error-Divs an,
 * falls sie noch nicht existieren.
 */
  private ensureDarkErrorPlaceholders(form: HTMLFormElement) {
    // Feld-Definitionen mit ihren Error-Keys
    const specs: {
      field: string;
      errors: { id: string; translateKey: string }[];
    }[] = [
        { field: 'name', errors: [{ id: 'nameError', translateKey: 'CONTACT.ERRORS.NAME_REQUIRED' }] },
        {
          field: 'email', errors: [
            { id: 'emailRequiredError', translateKey: 'CONTACT.ERRORS.EMAIL_REQUIRED' },
            { id: 'emailInvalidError', translateKey: 'CONTACT.ERRORS.EMAIL_INVALID' }
          ]
        },
        { field: 'subject', errors: [{ id: 'subjectError', translateKey: 'CONTACT.ERRORS.SUBJECT_REQUIRED' }] },
        { field: 'message', errors: [{ id: 'messageError', translateKey: 'CONTACT.ERRORS.MESSAGE_REQUIRED' }] },
      ];

    specs.forEach(({ field, errors }) => {
      // finde das .field-group zum Input mit name="{field}"
      const inputEl = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[formcontrolname="${field}"]`);
      if (!inputEl) return;
      const fg = inputEl.closest('.field-group')!;
      errors.forEach(({ id, translateKey }) => {
        if (fg.querySelector(`#${id}`)) return;  // schon da
        const err = this.renderer.createElement('div');
        this.renderer.setAttribute(err, 'id', id);
        this.renderer.addClass(err, 'error-msg');
        this.renderer.setStyle(err, 'display', 'none');
        this.renderer.setAttribute(err, 'data-translate', translateKey);
        this.renderer.appendChild(fg, err);
      });
    });

    // Übersetzungen für alle neuen Nodes anstoßen
    this.registerAll(form);

    const globalErrorId = 'submissionErrorGlobal';

    if (!form.querySelector(`#${globalErrorId}`)) {
      const globalErr = this.renderer.createElement('p');
      this.renderer.setAttribute(globalErr, 'id', globalErrorId);
      this.renderer.addClass(globalErr, 'error-msg');
      this.renderer.setStyle(globalErr, 'display', 'none');
      this.renderer.setProperty(globalErr, 'innerText', 'Es gab ein Problem beim Versenden. Bitte versuchen Sie es später erneut.');

      // z. B. ganz unten im Form einfügen:
      const btnWrapper = form.querySelector('.contact-btn');
      if (btnWrapper) {
        this.renderer.appendChild(btnWrapper, globalErr);
      }
    }
  }
  private toggleGlobalSubmissionError(form: HTMLFormElement, show: boolean) {
    const el = form.querySelector('#submissionErrorGlobal') as HTMLElement;
    if (el) this.renderer.setStyle(el, 'display', show ? 'block' : 'none');
  }

}

